import { useState, useEffect } from 'react';
import { collection, getDocs, getDoc, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faUtensils, faPalette, faBookOpen, faTrash, faEdit, faCheck, faTimes, faPlus, faCheckSquare, faSquare, faCheckCircle, faTimesCircle, faFilter } from '@fortawesome/free-solid-svg-icons';
import { getCategoryPhotoObject } from '../../lib/unsplash';
import UnsplashImage from '../../components/UnsplashImage';
import ImageSelector from '../../components/ImageSelector';
import { PageHeader, Button } from '../../design-system';

export default function ContentManager() {
  const [activeTab, setActiveTab] = useState('recipes'); // 'recipes' | 'activities' | 'stories'
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentCounts, setContentCounts] = useState({ recipes: 0, activities: 0, stories: 0 });
  const [editingItem, setEditingItem] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'approved' | 'pending'
  const itemsPerPage = 12;
  const { user } = useAuth();

  // Map tab names to Firestore type values
  const getContentType = (tab) => {
    const typeMap = {
      'recipes': 'recipe',
      'activities': 'activity',
      'stories': 'story'
    };
    return typeMap[tab] || 'recipe';
  };

  // Load content counts for all tabs on mount
  useEffect(() => {
    loadContentCounts();
  }, []);

  useEffect(() => {
    loadContent();
    setSelectedItems(new Set());
    setCurrentPage(1);
  }, [activeTab, statusFilter]);

  async function loadContentCounts() {
    try {
      const [recipesSnapshot, activitiesSnapshot, storiesSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'content'), where('type', '==', 'recipe'))),
        getDocs(query(collection(db, 'content'), where('type', '==', 'activity'))),
        getDocs(query(collection(db, 'content'), where('type', '==', 'story')))
      ]);

      setContentCounts({
        recipes: recipesSnapshot.size,
        activities: activitiesSnapshot.size,
        stories: storiesSnapshot.size
      });
    } catch (error) {
      console.error('Error loading content counts:', error);
    }
  }

  async function loadContent() {
    setLoading(true);
    try {
      const contentType = getContentType(activeTab);
      
      const contentQuery = query(
        collection(db, 'content'),
        where('type', '==', contentType)
      );
      const contentSnapshot = await getDocs(contentQuery);
      const allContent = contentSnapshot.docs.map(doc => {
        const data = doc.data();
        // Always use Firestore document ID, don't let data.id (from original content.js) overwrite it
        // Remove any id field from data to ensure we use the Firestore document ID
        const { id: _, ...dataWithoutId } = data;
        return {
          ...dataWithoutId,
          id: doc.id, // Firestore document ID (always use this for updates)
          firestoreId: doc.id // Explicitly store Firestore ID as backup
        };
      });

      // Filter by status if needed
      let filteredContent = allContent;
      if (statusFilter === 'approved') {
        filteredContent = allContent.filter(item => item.status === 'approved');
      } else if (statusFilter === 'pending') {
        filteredContent = allContent.filter(item => !item.status || item.status === 'pending');
      }

      setContent(filteredContent);
      // Update count for current tab (use allContent, not filtered)
      setContentCounts(prev => ({
        ...prev,
        [activeTab]: allContent.length
      }));
      setLoading(false);
    } catch (error) {
      console.error('Error loading content:', error);
      toast.error('Failed to load content');
      setLoading(false);
    }
  }

  function handleAddNew() {
    setEditingItem({
      type: getContentType(activeTab),
      title: '',
      description: '',
    });
    setShowEditor(true);
  }

  function handleEdit(item) {
    setEditingItem(item);
    setShowEditor(true);
  }

  async function handleDelete(itemId) {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await deleteDoc(doc(db, 'content', itemId));
      toast.success('Content deleted successfully');
      loadContent();
      loadContentCounts(); // Refresh counts
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Failed to delete content');
    }
  }

  async function handleBulkDelete() {
    if (selectedItems.size === 0) {
      toast.error('No items selected');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedItems.size} item(s)?`)) return;

    try {
      const deletePromises = Array.from(selectedItems).map(itemId => 
        deleteDoc(doc(db, 'content', itemId))
      );
      await Promise.all(deletePromises);
      toast.success(`Deleted ${selectedItems.size} item(s) successfully`);
      setSelectedItems(new Set());
      loadContent();
      loadContentCounts(); // Refresh counts
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error('Failed to delete items');
    }
  }

  async function handleToggleApproval(itemId, currentStatus) {
    try {
      const newStatus = currentStatus === 'approved' ? 'pending' : 'approved';
      await updateDoc(doc(db, 'content', itemId), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid
      });
      toast.success(`Content ${newStatus === 'approved' ? 'approved' : 'unapproved'}`);
      loadContent();
      loadContentCounts();
    } catch (error) {
      console.error('Error toggling approval:', error);
      toast.error('Failed to update approval status');
    }
  }

  async function handleBulkApprove() {
    if (selectedItems.size === 0) {
      toast.error('No items selected');
      return;
    }

    try {
      const updatePromises = Array.from(selectedItems).map(itemId => 
        updateDoc(doc(db, 'content', itemId), {
          status: 'approved',
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        })
      );
      await Promise.all(updatePromises);
      toast.success(`Approved ${selectedItems.size} item(s) successfully`);
      setSelectedItems(new Set());
      loadContent();
      loadContentCounts();
    } catch (error) {
      console.error('Error bulk approving:', error);
      toast.error('Failed to approve items');
    }
  }

  async function handleBulkUnapprove() {
    if (selectedItems.size === 0) {
      toast.error('No items selected');
      return;
    }

    try {
      const updatePromises = Array.from(selectedItems).map(itemId => 
        updateDoc(doc(db, 'content', itemId), {
          status: 'pending',
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        })
      );
      await Promise.all(updatePromises);
      toast.success(`Unapproved ${selectedItems.size} item(s) successfully`);
      setSelectedItems(new Set());
      loadContent();
      loadContentCounts();
    } catch (error) {
      console.error('Error bulk unapproving:', error);
      toast.error('Failed to unapprove items');
    }
  }

  function handleSelectItem(itemId) {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  }

  function handleSelectAll() {
    if (selectedItems.size === paginatedContent.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(paginatedContent.map(item => item.id)));
    }
  }

  const paginatedContent = content.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(content.length / itemsPerPage);

  async function handleSave(formData) {
    try {
      // If imageSearchQuery changed, fetch new photo object
      let unsplashPhoto = formData.unsplashPhoto;
      if (formData.imageSearchQuery && (!unsplashPhoto || formData.imageSearchQuery !== editingItem?.imageSearchQuery)) {
        const categoryMap = {
          'recipe': 'recipes',
          'activity': 'activities',
          'story': 'stories'
        };
        const category = categoryMap[formData.type] || 'recipes';
        const photo = await getCategoryPhotoObject(category, formData, 0);
        if (photo) {
          unsplashPhoto = photo;
          // Trigger download when admin saves with new image
          if (photo.links?.download_location) {
            const { triggerDownload } = await import('../../utils/unsplashUtils');
            await triggerDownload(photo.links.download_location);
          }
        }
      }

      const dataToSave = {
        ...formData,
        unsplashPhoto: unsplashPhoto || formData.unsplashPhoto || null,
        // Keep image URL for backwards compatibility
        image: unsplashPhoto?.urls?.regular || formData.image || null
      };

      // Use firestoreId if available, otherwise use id
      // This handles cases where content might have originalId from migration
      const documentId = editingItem.firestoreId || editingItem.id;
      
      // Validate that we have a valid Firestore document ID
      // Firestore IDs are typically 20 characters, alphanumeric, and don't contain special chars like '/'
      if (documentId && typeof documentId === 'string' && documentId.length > 0 && !documentId.includes('/')) {
        // Check if document exists before trying to update
        const docRef = doc(db, 'content', documentId);
        const docSnapshot = await getDoc(docRef);
        
        if (docSnapshot.exists()) {
          // Document exists, update it
          try {
            await updateDoc(docRef, {
              ...dataToSave,
              updatedAt: serverTimestamp(),
              updatedBy: user.uid
            });
            toast.success('Content updated successfully');
          } catch (error) {
            console.error('Error updating document:', error);
            throw error;
          }
        } else {
          // Document doesn't exist, create it instead
          console.warn(`Document ${documentId} not found in Firestore, creating new document instead`);
          await addDoc(collection(db, 'content'), {
            ...dataToSave,
            status: 'approved',
            createdAt: serverTimestamp(),
            createdBy: user.uid,
            totalClaps: 0,
            clappedBy: {},
            commentsCount: 0
          });
          toast.success('Content created successfully (was not found in Firestore)');
        }
      } else {
        // Create new
        await addDoc(collection(db, 'content'), {
          ...dataToSave,
          status: 'approved', // Admin-created content is auto-approved
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          totalClaps: 0,
          clappedBy: {},
          commentsCount: 0
        });
        toast.success('Content created successfully');
      }
      
      setShowEditor(false);
      setEditingItem(null);
      loadContent();
      loadContentCounts(); // Refresh counts after save
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
    }
  }

  const getTabIcon = (tab) => {
    if (tab === 'recipes') return faUtensils;
    if (tab === 'activities') return faPalette;
    return faBookOpen;
  };

  return (
    <div className="min-h-screen bg-background pt-14 pb-20 md:pt-0 md:pb-8">
      <PageHeader
        title="Content Manager"
        subtitle="Manage recipes, activities, and stories"
        actions={
          <Button
            onClick={handleAddNew}
            className="flex items-center gap-2 w-full md:w-auto"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="hidden sm:inline">Add New</span>
            <span className="capitalize">{getContentType(activeTab)}</span>
          </Button>
        }
        maxWidth="1280px"
      >
        {/* Tabs - Mobile: Horizontal scroll, Desktop: Flex */}
        <div className="-mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto no-scrollbar mt-4 md:mt-6">
          <div className="flex gap-3 md:gap-4 min-w-max md:min-w-0">
            {['recipes', 'activities', 'stories'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  pb-3 px-3 md:px-4 font-semibold border-b-4 transition-all capitalize flex items-center gap-2 touch-target whitespace-nowrap
                  ${activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary active:bg-gray-50'
                  }
                `}
              >
                <FontAwesomeIcon icon={getTabIcon(tab)} />
                {tab}
                <span className="text-xs md:text-sm bg-gray-100 text-text-secondary px-2 py-1 rounded-full">
                  {contentCounts[tab] || 0}
                </span>
              </button>
            ))}
          </div>
        </div>
      </PageHeader>

      {/* Content Grid - Mobile optimized spacing */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-8">
        {loading ? (
          <ContentLoadingSkeleton />
        ) : content.length === 0 ? (
          <EmptyState type={activeTab} onAddNew={handleAddNew} />
        ) : (
          <>
            {/* Filters and Bulk Actions - Mobile optimized */}
            <div className="mb-4 md:mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 md:gap-4 w-full md:w-auto">
                {/* Status Filter */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <FontAwesomeIcon icon={faFilter} className="text-text-secondary" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="flex-1 sm:flex-none px-3 py-2 border border-border rounded-lg text-sm bg-white text-text-primary focus:outline-none focus:border-primary touch-target"
                    style={{ fontSize: '16px' }}
                  >
                    <option value="all">All Status</option>
                    <option value="approved">Approved Only</option>
                    <option value="pending">Pending Only</option>
                  </select>
                </div>

                {/* Selection Controls */}
                {content.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleSelectAll}
                      className="text-text-secondary hover:text-text-primary transition-colors touch-target min-h-[44px] min-w-[44px] flex items-center justify-center"
                      title="Select all"
                    >
                      <FontAwesomeIcon icon={selectedItems.size === paginatedContent.length ? faCheckSquare : faSquare} />
                    </button>
                    <span className="text-sm text-text-secondary">
                      {selectedItems.size > 0 ? `${selectedItems.size} selected` : 'Select items'}
                    </span>
                  </div>
                )}

                {/* Bulk Actions - Mobile: Stack vertically */}
                {selectedItems.size > 0 && (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
                    <button
                      onClick={handleBulkApprove}
                      className="px-3 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-semibold hover:bg-green-100 transition-colors flex items-center justify-center gap-2 touch-target active:scale-95"
                    >
                      <FontAwesomeIcon icon={faCheckCircle} />
                      Approve ({selectedItems.size})
                    </button>
                    <button
                      onClick={handleBulkUnapprove}
                      className="px-3 py-2 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-lg text-sm font-semibold hover:bg-yellow-100 transition-colors flex items-center justify-center gap-2 touch-target active:scale-95"
                    >
                      <FontAwesomeIcon icon={faTimesCircle} />
                      Unapprove ({selectedItems.size})
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-semibold hover:bg-red-100 transition-colors flex items-center justify-center gap-2 touch-target active:scale-95"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      Delete ({selectedItems.size})
                    </button>
                  </div>
                )}
              </div>

              {/* Pagination Info */}
              {content.length > itemsPerPage && (
                <div className="text-sm text-text-secondary text-center md:text-left">
                  Page {currentPage} of {totalPages}
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {paginatedContent.map(item => (
                <ContentCard
                  key={item.id}
                  item={item}
                  onEdit={() => handleEdit(item)}
                  onDelete={() => handleDelete(item.id)}
                  onToggleApproval={() => handleToggleApproval(item.id, item.status)}
                  selected={selectedItems.has(item.id)}
                  onSelect={() => handleSelectItem(item.id)}
                />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="mt-4 md:mt-6 flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-3 border border-border rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed touch-target active:scale-95"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm md:text-base text-text-secondary">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-3 border border-border rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed touch-target active:scale-95"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Editor Modal */}
      <AnimatePresence>
        {showEditor && (
          <ContentEditorModal
            item={editingItem}
            onSave={handleSave}
            onClose={() => {
              setShowEditor(false);
              setEditingItem(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// Content Card Component
function ContentCard({ item, onEdit, onDelete, onToggleApproval, selected, onSelect }) {
  const [photoObject, setPhotoObject] = useState(item.unsplashPhoto || null);
  const [imageLoading, setImageLoading] = useState(!item.unsplashPhoto);

  useEffect(() => {
    async function loadImage() {
      // If item already has unsplashPhoto, use it
      if (item.unsplashPhoto) {
        setPhotoObject(item.unsplashPhoto);
        setImageLoading(false);
        return;
      }

      // Otherwise, fetch photo object
      setImageLoading(true);
      try {
        // Map singular type to plural category
        const categoryMap = {
          'recipe': 'recipes',
          'activity': 'activities',
          'story': 'stories'
        };
        const category = categoryMap[item.type] || 'recipes';
        
        // Use imageSearchQuery if available, otherwise use title
        if (item.imageSearchQuery || item.title) {
          const photo = await getCategoryPhotoObject(category, item, 0);
          setPhotoObject(photo);
        }
      } catch (error) {
        console.error('Error loading image:', error);
      } finally {
        setImageLoading(false);
      }
    }
    loadImage();
  }, [item]);

  const getTypeIcon = (type) => {
    if (type === 'recipe') return '🍽️';
    if (type === 'activity') return '🎨';
    return '📖';
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`bg-white rounded-2xl border-2 overflow-hidden shadow-sm hover:shadow-lg transition-all group ${
        selected ? 'border-primary' : 'border-border'
      }`}
    >
      {/* Image */}
      <div className="aspect-video bg-gradient-to-br from-primary/10 to-accent/10 relative overflow-hidden">
        {imageLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary"></div>
          </div>
        ) : photoObject ? (
          <UnsplashImage
            photo={photoObject}
            alt={item.title}
            className="w-full h-full object-cover"
            triggerDownloadOnClick={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl">
            {getTypeIcon(item.type)}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 md:p-5">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {onSelect && (
              <button
                onClick={onSelect}
                className="text-text-secondary hover:text-primary transition-colors touch-target min-h-[44px] min-w-[44px] flex items-center justify-center"
              >
                <FontAwesomeIcon icon={selected ? faCheckSquare : faSquare} />
              </button>
            )}
            <span className="text-xs font-bold text-primary uppercase tracking-wide">
              {item.type}
            </span>
            {/* Status Badge */}
            <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
              item.status === 'approved' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {item.status === 'approved' ? '✓ Approved' : '⏳ Pending'}
            </span>
          </div>
          {item.totalClaps > 0 && (
            <div className="flex items-center gap-1 text-sm">
              <span>👏</span>
              <span className="font-semibold">{item.totalClaps}</span>
            </div>
          )}
        </div>

        <h3 className="font-serif font-bold text-base md:text-lg text-text-primary mb-2 line-clamp-2">
          {item.title}
        </h3>

        <p className="text-sm text-text-secondary line-clamp-3 mb-4">
          {item.description}
        </p>

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {item.tags.slice(0, 3).map(tag => (
              <span
                key={tag}
                className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Actions - Mobile optimized */}
        <div className="flex flex-col gap-2 pt-4 border-t border-border">
          <div className="flex gap-2">
            <button
              onClick={onEdit}
              className="flex-1 bg-white border-2 border-border text-text-primary font-semibold py-3 md:py-2 rounded-xl hover:border-primary hover:text-primary transition-all flex items-center justify-center gap-2 touch-target active:scale-95"
            >
              <FontAwesomeIcon icon={faEdit} />
              Edit
            </button>
            <button
              onClick={onToggleApproval}
              className={`flex-1 font-semibold py-3 md:py-2 rounded-xl transition-all flex items-center justify-center gap-2 touch-target active:scale-95 ${
                item.status === 'approved'
                  ? 'bg-yellow-50 border-2 border-yellow-200 text-yellow-700 hover:bg-yellow-100'
                  : 'bg-green-50 border-2 border-green-200 text-green-700 hover:bg-green-100'
              }`}
              title={item.status === 'approved' ? 'Unapprove content' : 'Approve content'}
            >
              <FontAwesomeIcon icon={item.status === 'approved' ? faTimesCircle : faCheckCircle} />
              {item.status === 'approved' ? 'Unapprove' : 'Approve'}
            </button>
            <button
              onClick={onDelete}
              className="bg-white border-2 border-red-200 text-red-600 font-semibold px-4 py-3 md:py-2 rounded-xl hover:bg-red-50 hover:border-red-300 transition-all touch-target min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-95"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Content Editor Modal
function ContentEditorModal({ item, onSave, onClose }) {
  // Initialize formData with imageData if item has image info
  const initialFormData = item || {};
  const initialImageData = initialFormData.unsplashPhoto 
    ? { type: 'unsplash', unsplashPhoto: initialFormData.unsplashPhoto, url: initialFormData.image }
    : initialFormData.uploadedImageUrl
    ? { type: 'upload', uploadUrl: initialFormData.uploadedImageUrl, caption: initialFormData.imageCaption, url: initialFormData.uploadedImageUrl }
    : null;

  const [formData, setFormData] = useState({
    ...initialFormData,
    imageData: initialImageData
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle image selection (both Unsplash and upload)
  const handleImageSelect = (imageData) => {
    if (imageData) {
      setFormData(prev => ({
        ...prev,
        imageData: imageData, // Store complete image data
        image: imageData.url, // For backwards compatibility
        unsplashPhoto: imageData.type === 'unsplash' ? imageData.unsplashPhoto : null,
        uploadedImageUrl: imageData.type === 'upload' ? imageData.uploadUrl : null,
        imageCaption: imageData.caption || null,
        imageSearchQuery: imageData.type === 'unsplash' && imageData.unsplashPhoto ? '' : prev.imageSearchQuery // Clear search query if Unsplash selected
      }));
    } else {
      // Image removed
      setFormData(prev => ({
        ...prev,
        imageData: null,
        image: null,
        unsplashPhoto: null,
        uploadedImageUrl: null,
        imageCaption: null
      }));
    }
  };

  // Generate default search query based on content
  const getDefaultSearchQuery = () => {
    if (formData.imageSearchQuery) return formData.imageSearchQuery;
    if (formData.title) return formData.title;
    return '';
  };

  const handleArrayChange = (field, index, value) => {
    const newArray = [...(formData[field] || [])];
    newArray[index] = value;
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const handleAddArrayItem = (field) => {
    const newArray = [...(formData[field] || []), ''];
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const handleRemoveArrayItem = (field, index) => {
    const newArray = [...(formData[field] || [])];
    newArray.splice(index, 1);
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
      >
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-border p-6 flex items-center justify-between">
            <h2 className="text-2xl font-serif font-bold text-text-primary">
              {item.id ? 'Edit' : 'Create'} {formData.type}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Form Fields */}
          <div className="p-6 space-y-6">
            {/* Common Fields */}
            <FormField label="Title" required>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => handleChange('title', e.target.value)}
                className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
                required
              />
            </FormField>

            <FormField label="Description" required>
              <textarea
                value={formData.description || ''}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary resize-none"
                required
              />
            </FormField>

            {/* IMAGE SELECTOR */}
            <FormField label="Image">
              <ImageSelector
                onImageSelect={handleImageSelect}
                currentImage={formData.imageData}
                defaultQuery={getDefaultSearchQuery()}
              />
            </FormField>

            {/* Type-Specific Fields */}
            {formData.type === 'recipe' && (
              <RecipeFields formData={formData} onChange={handleChange} onArrayChange={handleArrayChange} onAddArrayItem={handleAddArrayItem} onRemoveArrayItem={handleRemoveArrayItem} />
            )}

            {formData.type === 'activity' && (
              <ActivityFields formData={formData} onChange={handleChange} onArrayChange={handleArrayChange} onAddArrayItem={handleAddArrayItem} onRemoveArrayItem={handleRemoveArrayItem} />
            )}

            {formData.type === 'story' && (
              <StoryFields formData={formData} onChange={handleChange} />
            )}

            {/* Tags */}
            <FormField label="Tags">
              <TagsInput
                value={formData.tags || []}
                onChange={(tags) => handleChange('tags', tags)}
              />
            </FormField>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-border p-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-white border-2 border-border text-text-primary font-semibold py-3 rounded-xl hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-gradient-to-r from-primary to-primary-light text-white font-semibold py-3 rounded-xl hover:shadow-lg transition-all"
            >
              {item.id ? 'Update' : 'Create'} {formData.type}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

// Recipe-specific fields
function RecipeFields({ formData, onChange, onArrayChange, onAddArrayItem, onRemoveArrayItem }) {
  return (
    <>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Prep Time">
          <input
            type="text"
            value={formData.prepTime || ''}
            onChange={(e) => onChange('prepTime', e.target.value)}
            placeholder="e.g., 30 minutes"
            className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
          />
        </FormField>
        <FormField label="Cook Time">
          <input
            type="text"
            value={formData.cookTime || ''}
            onChange={(e) => onChange('cookTime', e.target.value)}
            placeholder="e.g., 1 hour"
            className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
          />
        </FormField>
        <FormField label="Serves">
          <input
            type="number"
            value={formData.serves || ''}
            onChange={(e) => onChange('serves', parseInt(e.target.value))}
            className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
          />
        </FormField>
      </div>

      <FormField label="Difficulty">
        <select
          value={formData.difficulty || ''}
          onChange={(e) => onChange('difficulty', e.target.value)}
          className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
        >
          <option value="">Select difficulty</option>
          <option value="Easy">Easy</option>
          <option value="Medium">Medium</option>
          <option value="Hard">Hard</option>
        </select>
      </FormField>

      <ArrayField
        label="Ingredients"
        items={formData.ingredients || []}
        onChange={onArrayChange}
        onAdd={onAddArrayItem}
        onRemove={onRemoveArrayItem}
        fieldName="ingredients"
        placeholder="e.g., 2 cups flour"
      />

      <ArrayField
        label="Instructions"
        items={formData.instructions || []}
        onChange={onArrayChange}
        onAdd={onAddArrayItem}
        onRemove={onRemoveArrayItem}
        fieldName="instructions"
        placeholder="Step description"
        textarea
      />

      <FormField label="Cultural Context">
        <textarea
          value={formData.culturalContext || ''}
          onChange={(e) => onChange('culturalContext', e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary resize-none"
          placeholder="Share the story behind this recipe..."
        />
      </FormField>
    </>
  );
}

// Activity-specific fields
function ActivityFields({ formData, onChange, onArrayChange, onAddArrayItem, onRemoveArrayItem }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Time Needed">
          <input
            type="text"
            value={formData.time || ''}
            onChange={(e) => onChange('time', e.target.value)}
            placeholder="e.g., 20 minutes"
            className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
          />
        </FormField>
        <FormField label="Age Range">
          <input
            type="text"
            value={formData.ageRange || ''}
            onChange={(e) => onChange('ageRange', e.target.value)}
            placeholder="e.g., 4-8"
            className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
          />
        </FormField>
      </div>

      <FormField label="Category">
        <select
          value={formData.category || ''}
          onChange={(e) => onChange('category', e.target.value)}
          className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
        >
          <option value="">Select category</option>
          <option value="Crafts">Crafts</option>
          <option value="Games">Games</option>
          <option value="Learning">Learning</option>
          <option value="Holiday">Holiday</option>
        </select>
      </FormField>

      <ArrayField
        label="Materials Needed"
        items={formData.materials || []}
        onChange={onArrayChange}
        onAdd={onAddArrayItem}
        onRemove={onRemoveArrayItem}
        fieldName="materials"
        placeholder="e.g., Paper, markers, glue"
      />

      <ArrayField
        label="Instructions"
        items={formData.instructions || []}
        onChange={onArrayChange}
        onAdd={onAddArrayItem}
        onRemove={onRemoveArrayItem}
        fieldName="instructions"
        placeholder="Step description"
        textarea
      />

      <FormField label="Cultural Context">
        <textarea
          value={formData.culturalContext || ''}
          onChange={(e) => onChange('culturalContext', e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary resize-none"
          placeholder="Share why this activity is meaningful..."
        />
      </FormField>
    </>
  );
}

// Story-specific fields
function StoryFields({ formData, onChange }) {
  return (
    <>
      <FormField label="Story Text" required>
        <textarea
          value={formData.text || ''}
          onChange={(e) => onChange('text', e.target.value)}
          rows={10}
          className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary resize-none font-serif"
          placeholder="Write your story here..."
          required
        />
      </FormField>

      <FormField label="Age Appropriateness">
        <select
          value={formData.ageRange || ''}
          onChange={(e) => onChange('ageRange', e.target.value)}
          className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
        >
          <option value="">Select age range</option>
          <option value="3-5">3-5 years</option>
          <option value="6-8">6-8 years</option>
          <option value="9-12">9-12 years</option>
          <option value="All ages">All ages</option>
        </select>
      </FormField>

      <FormField label="Theme/Lesson">
        <input
          type="text"
          value={formData.theme || ''}
          onChange={(e) => onChange('theme', e.target.value)}
          placeholder="What children learn from this story"
          className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
        />
      </FormField>

      <FormField label="Cultural Significance">
        <textarea
          value={formData.culturalContext || ''}
          onChange={(e) => onChange('culturalContext', e.target.value)}
          rows={3}
          className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary resize-none"
          placeholder="Share the cultural background of this story..."
        />
      </FormField>
    </>
  );
}

// Reusable Components
function FormField({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-text-primary mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

function ArrayField({ label, items, onChange, onAdd, onRemove, fieldName, placeholder, textarea }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-text-primary mb-2">
        {label}
      </label>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex gap-2">
            {textarea ? (
              <textarea
                value={item}
                onChange={(e) => onChange(fieldName, index, e.target.value)}
                placeholder={placeholder}
                rows={2}
                className="flex-1 px-4 py-2 border-2 border-border rounded-xl focus:outline-none focus:border-primary resize-none"
              />
            ) : (
              <input
                type="text"
                value={item}
                onChange={(e) => onChange(fieldName, index, e.target.value)}
                placeholder={placeholder}
                className="flex-1 px-4 py-2 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
              />
            )}
            <button
              type="button"
              onClick={() => onRemove(fieldName, index)}
              className="px-3 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onAdd(fieldName)}
          className="w-full py-2 border-2 border-dashed border-border text-text-secondary rounded-xl hover:border-primary hover:text-primary transition-all"
        >
          + Add {label.slice(0, -1)}
        </button>
      </div>
    </div>
  );
}

function TagsInput({ value, onChange }) {
  const [input, setInput] = useState('');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = input.trim();
      if (tag && !value.includes(tag)) {
        onChange([...value, tag]);
        setInput('');
      }
    }
  };

  const removeTag = (tagToRemove) => {
    onChange(value.filter(tag => tag !== tagToRemove));
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {value.map(tag => (
          <span
            key={tag}
            className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-semibold"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="hover:text-primary-dark"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type and press Enter to add tags"
        className="w-full px-4 py-3 border-2 border-border rounded-xl focus:outline-none focus:border-primary"
      />
    </div>
  );
}

function EmptyState({ type, onAddNew }) {
  return (
    <div className="text-center py-16">
      <div className="text-6xl mb-4">📝</div>
      <h2 className="text-2xl font-serif font-bold text-text-primary mb-2">
        No {type} yet
      </h2>
      <p className="text-text-secondary mb-6">
        Create your first {type.slice(0, -1)} to get started
      </p>
      <button
        onClick={onAddNew}
        className="bg-gradient-to-r from-primary to-primary-light text-white font-semibold px-8 py-3 rounded-xl hover:shadow-lg transition-all"
      >
        Create {type.slice(0, -1)}
      </button>
    </div>
  );
}

function ContentLoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="bg-white rounded-2xl border border-border p-6 animate-pulse">
          <div className="aspect-video bg-gray-200 rounded-xl mb-4" />
          <div className="h-6 bg-gray-200 rounded mb-2" />
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-4" />
          <div className="h-10 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  );
}

